import type { MenuRoute, RouteAccessRequirement, UserAccessContext } from "@/@types/tab-navigation";
import { checkPermission, hasRouteAccess } from "@/shared/handle-permission";

const UNCATEGORIZED: string = "Sem Categoria";

export const isShownInModule = (item: MenuRoute, module: string, moduleAccessList: string[]): boolean =>
  moduleAccessList.includes(module) && !(item.hiddenInSidebarOf ?? []).some((hidden: string) => hidden === module);

const withOwnCategoryFirst = <T>(module: string, categories: Record<string, T[]>): Record<string, T[]> => {
  const { [module]: looseItems, ...otherCategories }: Record<string, T[]> = categories;
  if (!looseItems) return categories;

  return { [module]: looseItems, ...otherCategories };
};

export const groupRoutesByModule = <T extends MenuRoute>(
  items: T[],
  moduleAccessList: string[],
): Record<string, Record<string, T[]>> => {
  const grouped: Record<string, Record<string, T[]>> = items.reduce<Record<string, Record<string, T[]>>>(
    (acc: Record<string, Record<string, T[]>>, item: T) => {
      item.groupRequired
        .filter((module: string) => isShownInModule(item, module, moduleAccessList))
        .forEach((module: string) => {
          const category: string = item.category || UNCATEGORIZED;
          acc[module] = acc[module] ?? {};
          acc[module][category] = [...(acc[module][category] ?? []), item];
        });
      return acc;
    },
    {},
  );

  return Object.fromEntries(
    Object.entries(grouped).map(([module, categories]: [string, Record<string, T[]>]) => [
      module,
      withOwnCategoryFirst(module, categories),
    ]),
  );
};

export const canSeeMenuItem = (item: MenuRoute, context: UserAccessContext): boolean => {
  const hasRole: boolean = checkPermission({
    userRole: context.userRole,
    requiredAction: item.requiredAction,
    assignments: context.assignments,
    requiredRole: item.requiredRole,
  });
  if (!hasRole) return false;

  const tabs: RouteAccessRequirement[] = item.tabs ?? [];
  if (tabs.length === 0) return true;

  return tabs.some((tab: RouteAccessRequirement) => hasRouteAccess(tab, context));
};
